import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './CategoryHeader.module.css';
import { GRADEBOOK_SETTINGS } from './utils';
import { ROW_INDEX } from './constants';

/**
 * Format the "drops lowest grades" string for display
 * Matches V1 CategoryNameButton logic
 */
function formatDropLowestString(dropLowestNumGrades) {
  if (!dropLowestNumGrades) return null;
  const gradeStr = dropLowestNumGrades > 1 ? 'grades' : 'grade';
  const displayNumber =
    dropLowestNumGrades === 1 ? '' : dropLowestNumGrades + ' ';
  return `drops ${displayNumber}lowest ${gradeStr}`;
}

export function CategoryHeader({
  categories,
  categoryGroups,
  collapsedCategories,
  toggleCategory,
  stickyColumns,
  scrollableColumns,
  virtualColumns,
  paddingLeft,
  paddingRight,
  gradingType,
  columnIndexMap,
  focusedCell,
  onCellClick,
}) {
  const hasStickyColumns = stickyColumns.length > 0;
  // Build a map of category info for each category ID
  const categoryInfoMap = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      const assignments = categoryGroups?.get(category.id) || [];
      const canCollapse = assignments.length >= 1;
      // Find the first column ID for this category
      const firstColumnId =
        assignments[0]?.id || `category-total-${category.id}`;
      map.set(category.id, { category, canCollapse, firstColumnId });
    });
    return map;
  }, [categories, categoryGroups]);

  // Helper to get category ID from a column
  const getCategoryId = (column) => {
    if (!column) return null;
    if (column.meta?.category) return column.meta.category;
    if (column.id?.startsWith('category-total-')) {
      return column.id.replace('category-total-', '');
    }
    return null;
  };

  // Compute which column should show the category label (first visible column of each category)
  const firstVisibleColumnOfCategory = useMemo(() => {
    const map = new Map(); // categoryId -> columnId
    for (const virtualColumn of virtualColumns) {
      const column = scrollableColumns[virtualColumn.index];
      if (!column) continue;
      const categoryId = getCategoryId(column);
      if (categoryId && !map.has(categoryId)) {
        map.set(categoryId, column.id);
      }
    }
    return map;
  }, [virtualColumns, scrollableColumns]);

  const isStickyFocused =
    focusedCell?.rowIndex === ROW_INDEX.CATEGORY &&
    focusedCell?.columnId === 'name';

  return (
    <tr
      role="row"
      aria-rowindex={1}
      style={{ height: 40, backgroundColor: 'var(--white)' }}
    >
      {/* Sticky name column - only when there are students */}
      {hasStickyColumns && (
        <th
          className={`${styles.stickyEmptyCell} ${isStickyFocused ? styles.focusedCell : ''}`}
          style={{
            position: 'sticky',
            left: 0,
            width: 250,
            minWidth: 250,
            maxWidth: 250,
          }}
          role="columnheader"
          scope="col"
          tabIndex={isStickyFocused ? 0 : -1}
          aria-colindex={1}
          data-row-index={ROW_INDEX.CATEGORY}
          data-column-id="name"
          data-grid-id="r0-c0"
          onClick={() => onCellClick?.(ROW_INDEX.CATEGORY, 'name')}
        >
          <span className={styles.srOnly}>Student Name Column</span>
        </th>
      )}

      {/* Padding for horizontal scroll - always render for stable layout */}
      <th
        aria-hidden="true"
        style={{
          width: paddingLeft,
          minWidth: paddingLeft,
          maxWidth: paddingLeft,
          padding: 0,
          border: 'none',
        }}
      />

      {/* Render individual cells for each virtual column */}
      {virtualColumns.map((virtualColumn) => {
        const column = scrollableColumns[virtualColumn.index];
        if (!column) return <th key={`empty-${virtualColumn.index}`} />;

        const columnId = column.id;
        const isFocused =
          focusedCell?.rowIndex === ROW_INDEX.CATEGORY &&
          focusedCell?.columnId === columnId;
        const gridColIndex = columnIndexMap?.get(columnId) ?? 0;

        // Handle studentId column
        if (columnId === 'studentId') {
          return (
            <th
              key="studentId"
              className={`${styles.studentIdHeaderCell} ${isFocused ? styles.focusedCell : ''}`}
              role="columnheader"
              scope="col"
              tabIndex={isFocused ? 0 : -1}
              aria-colindex={gridColIndex + 1}
              style={{
                width: `${virtualColumn.size}px`,
                minWidth: `${virtualColumn.size}px`,
                maxWidth: `${virtualColumn.size}px`,
              }}
              data-row-index={ROW_INDEX.CATEGORY}
              data-column-id="studentId"
              data-grid-id={`r0-c${gridColIndex}`}
              onClick={() => onCellClick?.(ROW_INDEX.CATEGORY, 'studentId')}
            >
              <span className={styles.srOnly}>Student ID Column</span>
            </th>
          );
        }

        // Handle courseTotal column
        if (columnId === 'courseTotal') {
          return (
            <th
              key="courseTotal"
              className={`${styles.courseTotalCell} ${isFocused ? styles.focusedCell : ''}`}
              role="columnheader"
              scope="col"
              tabIndex={isFocused ? 0 : -1}
              aria-colindex={gridColIndex + 1}
              style={{
                width: `${virtualColumn.size}px`,
                minWidth: `${virtualColumn.size}px`,
                maxWidth: `${virtualColumn.size}px`,
              }}
              data-row-index={ROW_INDEX.CATEGORY}
              data-column-id="courseTotal"
              data-grid-id={`r0-c${gridColIndex}`}
              onClick={() => onCellClick?.(ROW_INDEX.CATEGORY, 'courseTotal')}
            >
              <span className={styles.srOnly}>Course Total Column</span>
            </th>
          );
        }

        // Handle category columns (assignments and category totals)
        const categoryId = getCategoryId(column);
        const categoryInfo = categoryId
          ? categoryInfoMap.get(categoryId)
          : null;
        const category = categoryInfo?.category;
        const canCollapse = categoryInfo?.canCollapse || false;
        const dropLowestStr = formatDropLowestString(
          category?.dropLowestNumGrades,
        );

        // Check if this is the first visible cell of this category (show label here)
        const isFirstCellOfCategory =
          categoryId &&
          firstVisibleColumnOfCategory.get(categoryId) === columnId;

        // Determine if this is a category-total column
        const isCategoryTotal = columnId?.startsWith('category-total-');

        const isCollapsed = collapsedCategories.has(categoryId);

        const cellClasses = [
          styles.categoryCell,
          isFocused ? styles.focusedCell : '',
          isCategoryTotal ? styles.categoryTotalCell : '',
          isCollapsed && isFirstCellOfCategory
            ? styles.categoryCellCollapsed
            : '',
        ]
          .filter(Boolean)
          .join(' ');

        const handleClick = () => {
          onCellClick?.(ROW_INDEX.CATEGORY, columnId);
        };

        // Note: Enter/Space keyboard handling is done by the keyboard navigation hook
        // (via onCellActivate -> handleCellActivate -> toggleCategory)

        return (
          <th
            key={columnId}
            className={cellClasses}
            onClick={handleClick}
            role="columnheader"
            scope="col"
            tabIndex={isFocused ? 0 : -1}
            aria-colindex={gridColIndex + 1}
            aria-expanded={
              canCollapse && isFirstCellOfCategory
                ? !collapsedCategories.has(categoryId)
                : undefined
            }
            aria-label={
              isFirstCellOfCategory && category
                ? canCollapse
                  ? `${category.name} category. Press Enter to ${collapsedCategories.has(categoryId) ? 'expand' : 'collapse'}`
                  : `${category.name} category`
                : category
                  ? isCategoryTotal
                    ? `${category.name} Total`
                    : `${category.name} category`
                  : undefined
            }
            style={{
              width: `${virtualColumn.size}px`,
              minWidth: `${virtualColumn.size}px`,
              maxWidth: `${virtualColumn.size}px`,
            }}
            data-row-index={ROW_INDEX.CATEGORY}
            data-column-id={columnId}
            data-category-id={categoryId}
            data-grid-id={`r0-c${gridColIndex}`}
          >
            {/* Only show category content in the first cell of each category */}
            {isFirstCellOfCategory && category && (
              <div className={styles.categoryContent}>
                {canCollapse && (
                  <button
                    type="button"
                    className={styles.expandButton}
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCellClick?.(ROW_INDEX.CATEGORY, columnId);
                      toggleCategory(categoryId);
                    }}
                    aria-label={`${collapsedCategories.has(categoryId) ? 'Expand' : 'Collapse'} ${category.name}`}
                  >
                    <svg
                      className={styles.expandIcon}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {collapsedCategories.has(categoryId) ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      )}
                    </svg>
                  </button>
                )}
                <span className={styles.categoryName}>{category.name}</span>
                {gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted && (
                  <span className={styles.weights}>
                    {category.weight}% of Total
                  </span>
                )}
                {dropLowestStr && (
                  <i className={styles.dropLowest}>({dropLowestStr})</i>
                )}
              </div>
            )}
            {!isFirstCellOfCategory && category && (
              <span className={styles.srOnly}>
                {isCategoryTotal
                  ? `${category.name} Total`
                  : `${category.name} category`}
              </span>
            )}
          </th>
        );
      })}

      {/* Padding for horizontal scroll (right) - for columns after visible ones */}
      <th
        aria-hidden="true"
        style={{
          width: paddingRight,
          minWidth: paddingRight,
          maxWidth: paddingRight,
          padding: 0,
          border: 'none',
        }}
      />
    </tr>
  );
}

CategoryHeader.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      weight: PropTypes.number,
      dropLowestNumGrades: PropTypes.number,
    }),
  ).isRequired,
  categoryGroups: PropTypes.instanceOf(Map),
  collapsedCategories: PropTypes.instanceOf(Set).isRequired,
  toggleCategory: PropTypes.func.isRequired,
  stickyColumns: PropTypes.array,
  scrollableColumns: PropTypes.array.isRequired,
  virtualColumns: PropTypes.array.isRequired,
  paddingLeft: PropTypes.number.isRequired,
  paddingRight: PropTypes.number.isRequired,
  gradingType: PropTypes.number,
  columnIndexMap: PropTypes.instanceOf(Map),
  focusedCell: PropTypes.shape({
    rowIndex: PropTypes.number,
    columnId: PropTypes.string,
  }),
  onCellClick: PropTypes.func,
};

CategoryHeader.defaultProps = {
  categoryGroups: new Map(),
  stickyColumns: [],
  gradingType: GRADEBOOK_SETTINGS.gradingTypePoints,
  columnIndexMap: null,
  focusedCell: null,
  onCellClick: () => {},
};
